/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { Suspense, useMemo } from "react";
import { useSuspenseQueries } from "@tanstack/react-query";
import { companyQueryOptions } from "../lib/react-query/useCompany";
import { configQueryOptions } from "../lib/react-query/useConfig";
import { pluginsQueryOptions } from "../lib/react-query/usePlugins";
import { translationQueryOptions } from "../lib/react-query/useTranslation";
import { useSuspenseToken } from "../lib/react-query/useToken";

import { updateAxiosHeaders } from "../axios.init";

import LoadingIndicator from "../components/LoadingIndicator";

const AppDataLoader = ({ children }) => {
  const pageLang = document.documentElement.lang;
  const queries = useMemo(
    () => [configQueryOptions(), pluginsQueryOptions(), translationQueryOptions(pageLang), companyQueryOptions()],
    [pageLang]
  );

  useSuspenseQueries({ queries });

  return children;
};

const TokenInitializer = ({ children, companyName, companyKey }) => {
  const tokenParams = useMemo(
    () => ({
      company: companyName,
      key: companyKey,
    }),
    [companyName, companyKey]
  );

  const { data: token } = useSuspenseToken(tokenParams);

  if (token) {
    updateAxiosHeaders({
      "X-Company-Login": companyName,
      "X-Token": token,
    });
  }

  return (
    <Suspense fallback={<LoadingIndicator page />}>
      <AppDataLoader>{children}</AppDataLoader>
    </Suspense>
  );
};

export const AppInitializer = ({ children, companyName, companyKey }) => {
  return (
    <TokenInitializer companyName={companyName} companyKey={companyKey}>
      {children}
    </TokenInitializer>
  );
};
